import { format } from 'date-fns'
import { sortBy, sortedLastIndex } from 'lodash-es'

import { CalendarEventWithPlacementIndices } from './'

const getPlacementIndexForDate = (
  calendarEvent: CalendarEventWithPlacementIndices,
  date: Date,
) => {
  const formattedDate = format(date, 'yyyy-MM-dd')

  if (!calendarEvent?.placementIndices) {
    return -1
  }

  const index =
    sortedLastIndex(
      Object.keys(calendarEvent.placementIndices),
      formattedDate,
    ) - 1

  return Object.values(calendarEvent.placementIndices)[index]
}

export const getCalendarEventsForDate = (
  date: Date,
  calendarEvents: CalendarEventWithPlacementIndices[],
): CalendarEventWithPlacementIndices[] => {
  const formattedDate = format(date, 'yyyy-MM-dd')

  const events: CalendarEventWithPlacementIndices[] = calendarEvents
    .filter(
      event =>
        event.formattedStartDate <= formattedDate &&
        event.formattedEndDate >= formattedDate,
    )
    .map(event => ({
      ...event,
      placementIndex: getPlacementIndexForDate(event, date),
    }))

  return sortBy(events, 'placementIndex') as CalendarEventWithPlacementIndices[]
}

export const setInitialPlacementIndices = (
  date: Date,
  calendarEvents: CalendarEventWithPlacementIndices[],
) => {
  const formattedDate = format(date, 'yyyy-MM-dd')

  const calendarEventsForDate = getCalendarEventsForDate(date, calendarEvents)

  const map = new Map()

  const calendarEventWithPlacementIndex = calendarEventsForDate.filter(
    event => event.placementIndex !== -1,
  )

  calendarEventWithPlacementIndex.forEach(event => {
    map.set(event.id, event.placementIndex)
  })

  const calendarEventsWithNoPlacementIndex = calendarEventsForDate.filter(
    event => event.placementIndex === -1,
  )

  calendarEventsWithNoPlacementIndex.forEach(unplacedEvent => {
    const eventIds = calendarEventsForDate.map(event => event.id)

    let placementIndex = 0
    // eslint-disable-next-line no-constant-condition
    while (true) {
      let indexIsOccupied = false
      for (let i = 0; i < eventIds.length; i += 1) {
        const eventId = eventIds[i]

        if (map.get(eventId) === placementIndex) {
          indexIsOccupied = true
        }
      }

      if (!indexIsOccupied) {
        break
      }

      placementIndex += 1
    }

    map.set(unplacedEvent.id, placementIndex)

    const indexToUpdate = calendarEvents.findIndex(
      event => event.id === unplacedEvent.id,
    )

    calendarEvents[indexToUpdate] = {
      ...unplacedEvent,
      placementIndices: {
        [formattedDate]: placementIndex,
      },
    }
  })
}

export const updatePlacementIndices = (
  date: Date,
  calendarEvents: CalendarEventWithPlacementIndices[],
) => {
  const formattedDate = format(date, 'yyyy-MM-dd')

  const calendarEventsForDate = getCalendarEventsForDate(date, calendarEvents)

  const occupiedIndices = calendarEventsForDate.map(
    event => event.placementIndex,
  )

  const availablePlacementIndices = [0, 1, 2].filter(
    availableIndex => !occupiedIndices.includes(availableIndex),
  )

  const hiddenEvents = calendarEventsForDate.filter(
    event => (event?.placementIndex || -1) >= 3,
  )

  if (hiddenEvents.length > 0 && availablePlacementIndices.length > 0) {
    hiddenEvents.forEach(hiddenEvent => {
      const indexToUpdate = calendarEvents.findIndex(
        vacation => vacation.id === hiddenEvent.id,
      )

      calendarEvents[indexToUpdate] = {
        ...hiddenEvent,
        placementIndices: {
          ...hiddenEvent.placementIndices,
          [formattedDate]: availablePlacementIndices.shift() as number,
        },
      }

      if (availablePlacementIndices.length === 0) {
        return
      }
    })
  }
}
